import { useState } from "react";
import { CalendarDays, CheckCircle2, Plus, Maximize2 } from "lucide-react";
import { useLifeState } from "@/context/LifeStateContext";
import { planningMeetings, horizons, lifePlanKey, iso, uid, pointsFor, stripHtml, areas, priorities } from "@/lib/life";
import { PageHeader, SectionCard, ProgressBar } from "@/components/shared";
import { RichTextEditor } from "@/components/RichTextEditor";
import { FormModal, TextField, SelectField } from "@/components/forms";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const areaOpts = areas.map((a) => ({ value: a.name, label: a.name }));
const prioOpts = priorities.map((p) => ({ value: p, label: p }));

export default function LifePlanning() {
  const { state, update, loading } = useLifeState();
  const [horizon, setHorizon] = useState("Week");
  const [date, setDate] = useState(iso());
  const [expanded, setExpanded] = useState(null); // prompt object
  const [actionDraft, setActionDraft] = useState(null);

  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const meeting = planningMeetings[horizon];
  const key = lifePlanKey(horizon, date);
  const answers = state.lifePlans?.[key]?.answers || {};
  const answered = meeting.prompts.filter((p) => stripHtml(answers[p.id])).length;

  const setAnswer = (pid, val) => update((prev) => {
    const plans = { ...(prev.lifePlans || {}) };
    const cur = plans[key] || { answers: {} };
    plans[key] = { answers: { ...cur.answers, [pid]: val }, updatedAt: new Date().toISOString() };
    return { ...prev, lifePlans: plans };
  }, `Updated ${horizon} plan`);

  const openAction = (prompt) => setActionDraft({ id: uid("a"), title: "", date: iso(), time: "09:00", engine: areas[0].name, priority: "Medium", from: prompt.title });
  const saveAction = () => {
    if (!actionDraft.title.trim()) return toast.error("Give the action a title");
    const item = { id: actionDraft.id, title: actionDraft.title.trim(), date: actionDraft.date, time: actionDraft.time, engine: actionDraft.engine, priority: actionDraft.priority, points: pointsFor(actionDraft.priority), done: false };
    update((prev) => ({ ...prev, tasks: [...(prev.tasks || []), item] }), "Added action from planning");
    setActionDraft(null);
    toast.success("Added to your Command actions");
  };

  return (
    <div>
      <PageHeader title="Life Planning" subtitle="Turn vision into daily action through faithful, unhurried planning meetings." testid="life-planning-header"
        actions={<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 bg-white border-[color:var(--border-subtle)]" data-testid="planning-date" />} />

      <div className="flex flex-wrap items-center gap-1.5 mb-4 rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-1 w-max" data-testid="planning-horizon-switcher">
        {horizons.map((h) => (
          <button key={h} onClick={() => setHorizon(h)} data-testid={`planning-horizon-${h}`}
            className={cn("px-3.5 py-1.5 rounded-[9px] text-sm transition-colors", horizon === h ? "bg-[var(--green)] text-[#f7fbf9]" : "text-[var(--text-muted)] hover:text-[var(--text)]")}>{h}</button>
        ))}
      </div>

      <SectionCard className="mb-4" testid="planning-purpose-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-[10px] bg-[color:rgba(198,139,60,0.14)] text-[var(--gold)] shrink-0"><CalendarDays className="h-5 w-5" /></span>
            <div><h3 className="font-serif-display text-xl text-[var(--text)]">{meeting.title}</h3><p className="text-sm text-[var(--text-muted)] mt-1 max-w-2xl">{meeting.purpose}</p></div>
          </div>
          <div className="min-w-[160px]">
            <div className="flex items-center justify-between text-xs text-[var(--text-muted)] mb-1"><span>Prepared</span><span className="tabular-nums">{answered}/{meeting.prompts.length}</span></div>
            <ProgressBar value={answered} max={meeting.prompts.length} tone="green" />
          </div>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {meeting.prompts.map((p) => (
          <SectionCard key={p.id} title={p.title} subtitle={p.help} testid={`planning-prompt-${p.id}`}
            right={<Button variant="ghost" size="sm" onClick={() => openAction(p)} data-testid={`planning-add-action-${p.id}`} className="text-[var(--green)] h-7"><Plus className="h-3.5 w-3.5 mr-1" />Add action</Button>}>
            <RichTextEditor
              value={answers[p.id] || ""}
              onChange={(html) => setAnswer(p.id, html)}
              placeholder="Reflect and write… use bold and lists to break it into steps"
              onExpand={() => setExpanded(p)}
              testid={`planning-input-${p.id}`}
            />
            {stripHtml(answers[p.id]) && <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-[var(--green)]"><CheckCircle2 className="h-3 w-3" />Saved</div>}
          </SectionCard>
        ))}
      </div>

      {/* Detached full-screen editor */}
      <Dialog open={!!expanded} onOpenChange={(o) => !o && setExpanded(null)}>
        <DialogContent className="max-w-3xl" data-testid="planning-expand-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-xl">{expanded?.title}</DialogTitle></DialogHeader>
          {expanded && (
            <div>
              <p className="text-sm text-[var(--text-muted)] mb-3">{expanded.help}</p>
              <RichTextEditor value={answers[expanded.id] || ""} onChange={(html) => setAnswer(expanded.id, html)} placeholder="Write freely here…" minHeight={340} testid="planning-expand-editor" />
              <div className="flex justify-end mt-4">
                <Button onClick={() => setExpanded(null)} className="bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]" data-testid="planning-expand-done">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Turn a planning note into a real action */}
      {actionDraft && (
        <FormModal open={!!actionDraft} onOpenChange={(o) => !o && setActionDraft(null)} title="Add action from planning" onSubmit={saveAction} testid="planning-action-modal">
          <TextField label="Action" value={actionDraft.title} onChange={(e) => setActionDraft({ ...actionDraft, title: e.target.value })} full testid="planning-action-title" placeholder="e.g. Draft dissertation section" />
          <SelectField label="Life area" value={actionDraft.engine} onChange={(v) => setActionDraft({ ...actionDraft, engine: v })} options={areaOpts} testid="planning-action-area" />
          <SelectField label="Priority" value={actionDraft.priority} onChange={(v) => setActionDraft({ ...actionDraft, priority: v })} options={prioOpts} testid="planning-action-priority" />
          <TextField label="Date" type="date" value={actionDraft.date} onChange={(e) => setActionDraft({ ...actionDraft, date: e.target.value })} testid="planning-action-date" />
        </FormModal>
      )}
    </div>
  );
}
